/**
 * Design system component: Layout
 *
 * Root page wrapper used by all protected screens. Provides:
 *   - A centred responsive container (420px mobile / 720px tablet / 1000px desktop)
 *   - A sticky frosted-glass header with logo mark + centred title, optional left
 *     action (back arrow), notification bell, and responsive navigation
 *   - An optional `headerContent` slot below the title row for per-page
 *     content such as search bars, filter tab strips, and stats strips
 *   - A `children` content area with appropriate horizontal padding
 *   - `BottomNav` for mobile viewports (fixed, below 768px)
 *   - Top nav links integrated into the header right zone for ≥768px viewports
 *
 * The logo mark (logo-transparent.svg, 28px) and page title are centred together
 * as a unit using absolute positioning within the title row, so they remain
 * centred regardless of the left/right zone widths (§2.2).
 *
 * Header shadow activates only on scroll (§2.2: "shadow on scroll").
 *
 * The frosted-glass effect uses `background: rgba(255,255,255,0.94)` with
 * `backdrop-filter: blur(12px)` per the sticky header spec in REDESIGN_BRIEF.md §2.2.
 *
 * Navigation spec: REDESIGN_BRIEF.md v3.1 §2.7
 */
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { T, SPACE, CONTAINER, BREAKPOINT } from '@/lib/tokens';
import { BottomNav } from './BottomNav';
import { NotificationCenter } from '@/components/NotificationCenter';
import { useBreakpoint } from '@/hooks/useMobile';
import logoTransparent from '@/assets/logo-transparent.svg';

// Top nav destinations — identical to BottomNav but rendered in the header.
const HEADER_NAV_LINKS = [
  { path: '/dashboard', label: 'Groups' },
  { path: '/my-items', label: 'My Items' },
  { path: '/account', label: 'Account' },
] as const;

/**
 *
 * @param path
 * @param pathname
 */
function isLinkActive(path: string, pathname: string): boolean {
  if (path === '/dashboard') {
    return pathname === '/dashboard' || pathname.startsWith('/groups/');
  }
  return pathname === path;
}

interface LayoutProps {
  /** Page title displayed centred in the header alongside the logo mark. */
  title: string;
  /** Optional style overrides for the title element (e.g. font size, truncation). */
  titleStyle?: CSSProperties;
  /** Optional left-side action element (e.g. back arrow). */
  leftAction?: ReactNode;
  /** Content rendered below the header inside the responsive container. */
  children: ReactNode;
  /**
   * Content rendered inside the sticky header, below the title row.
   * Used for search bars, filter tabs, stats strips, action buttons, etc.
   */
  headerContent?: ReactNode;
}

/**
 *
 * @param root0
 * @param root0.title
 * @param root0.titleStyle
 * @param root0.leftAction
 * @param root0.children
 * @param root0.headerContent
 */
export function Layout({ title, titleStyle, leftAction, children, headerContent }: LayoutProps) {
  const bp = useBreakpoint();
  const location = useLocation();
  const navigate = useNavigate();
  const maxWidth =
    bp === 'desktop' ? CONTAINER.desktop : bp === 'tablet' ? CONTAINER.tablet : CONTAINER.mobile;
  const hPad = bp === 'desktop' ? 32 : bp === 'tablet' ? 24 : SPACE.screenPadH;

  const [showTopNav, setShowTopNav] = useState(() => window.innerWidth >= BREAKPOINT.nav);

  // Shadow appears only when the page is scrolled (§2.2).
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleResize = () => setShowTopNav(window.innerWidth >= BREAKPOINT.nav);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: T.bg,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth,
          background: T.surface,
          minHeight: '100vh',
          position: 'relative',
        }}
      >
        {/* Sticky header */}
        <div
          style={{
            padding: `calc(env(safe-area-inset-top, 0px) + ${SPACE.screenPadTop}px) ${hPad}px 0`,
            borderBottom: `1px solid ${T.border}`,
            background: 'rgba(255,255,255,0.94)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: scrolled ? '0 1px 4px rgba(0,0,0,0.03)' : 'none',
            transition: 'box-shadow 0.2s',
            position: 'sticky',
            top: 0,
            zIndex: 40,
          }}
        >
          {/* Title row — uses position:relative so the logo+title unit can be
              absolutely centred regardless of left/right zone widths. */}
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              marginBottom: headerContent ? 16 : 20,
              minHeight: SPACE.minTapTarget,
            }}
          >
            {/* Left action or implicit spacer */}
            <div style={{ position: 'relative', zIndex: 1, flexShrink: 0, width: 44 }}>
              {leftAction ?? null}
            </div>

            {/* Logo + title — absolutely centred across the full row width.
                pointer-events:none ensures left/right zones remain interactive. */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 8,
                pointerEvents: 'none',
              }}
            >
              <img
                src={logoTransparent}
                alt=""
                aria-hidden
                style={{ width: 28, height: 28, display: 'block', flexShrink: 0 }}
              />
              <h1
                style={{
                  margin: 0,
                  fontSize: 18,
                  color: T.text,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '55vw',
                  ...titleStyle,
                }}
              >
                {title}
              </h1>
            </div>

            {/* Right zone: top nav links (desktop) + notification bell.
                marginLeft:auto pushes it to the right edge. */}
            <div
              style={{
                marginLeft: 'auto',
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                flexShrink: 0,
              }}
            >
              {showTopNav && (
                <nav
                  aria-label="Main navigation"
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  {HEADER_NAV_LINKS.map(({ path, label }) => {
                    const active = isLinkActive(path, location.pathname);
                    return (
                      <button
                        key={path}
                        onClick={() => navigate(path)}
                        aria-current={active ? 'page' : undefined}
                        style={{
                          background: 'none',
                          border: 'none',
                          borderBottom: `2px solid ${active ? T.accent : 'transparent'}`,
                          padding: '4px 10px 6px',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          fontSize: 14,
                          fontWeight: active ? 600 : 500,
                          color: active ? T.accent : T.textSec,
                          whiteSpace: 'nowrap',
                          transition: 'color 0.15s, border-color 0.15s',
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </nav>
              )}
              <NotificationCenter />
            </div>
          </div>

          {/* Optional sub-header content */}
          {headerContent}
        </div>

        {/* Page content — extra bottom padding on mobile to clear fixed BottomNav */}
        <div
          style={{
            padding: `8px ${hPad}px`,
            paddingBottom: showTopNav ? 40 : 80,
          }}
        >
          {children}
        </div>
      </div>

      {/* Mobile bottom navigation bar (fixed, below 768px) */}
      <BottomNav />
    </div>
  );
}
